import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { TourAlertDialog, type TourStep, useTour } from "@/components/tour";
import { TOUR_STEP_IDS } from "@/lib/tour-constants";
import { ActivePromotionsList } from "./components/active-promotions-list";
import { KpiCards } from "./components/kpi-cards";
import { MenuGrowthChart } from "./components/menu-growth-chart";
import { RecentProductsTable } from "./components/recent-products-table";
import { SetupChecklist } from "./components/setup-checklist";
import { SubscriptionCard } from "./components/subscription-card";
import { useHomeData } from "./use-home-data";

const TOUR_OPEN_DELAY = 100;

export default function Page() {
  const { t } = useTranslation();
  const { setSteps, isTourCompleted } = useTour();
  const [openTour, setOpenTour] = useState(false);
  const data = useHomeData();

  const steps: TourStep[] = useMemo(
    () => [
      {
        content: <div className="p-2">{t("tour.homeTour.step1")}</div>,
        selectorId: TOUR_STEP_IDS.SETTINGS_BUTTON,
        position: "right",
      },
    ],
    [t],
  );

  useEffect(() => {
    if (localStorage.getItem("home_tour_completed") === "true") {
      return;
    }

    setSteps(steps);
    const timer = setTimeout(() => {
      setOpenTour(true);
    }, TOUR_OPEN_DELAY);

    return () => clearTimeout(timer);
  }, [setSteps, isTourCompleted, steps]);

  if (data.isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <div className="grid grid-cols-1 gap-4 px-4 @xl/main:grid-cols-2 @5xl/main:grid-cols-4 lg:px-6">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
        <div className="grid grid-cols-1 gap-4 px-4 @xl/main:grid-cols-3 lg:px-6">
          <Skeleton className="h-[300px] w-full @xl/main:col-span-2" />
          <Skeleton className="h-[300px] w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <KpiCards
        businessType={data.businessType}
        kpis={data.kpis}
        subscription={data.subscription}
      />

      <SetupChecklist businessType={data.businessType} state={data.checklist} />

      <div className="grid grid-cols-1 gap-4 px-4 @xl/main:grid-cols-3 lg:px-6">
        <div className="@xl/main:col-span-2">
          <MenuGrowthChart chart={data.chart} />
        </div>
        <SubscriptionCard
          businessType={data.businessType}
          organizationSlug={data.organizationSlug}
          subscription={data.subscription}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 @xl/main:grid-cols-2 lg:px-6">
        <RecentProductsTable currency={data.currency} products={data.recentProducts} />
        <ActivePromotionsList promotions={data.activePromotions} />
      </div>

      <TourAlertDialog isOpen={openTour} setIsOpen={setOpenTour} />
    </div>
  );
}
